import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAppNumber } from "../feature/app.Slice";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { getContactEmailByAppNumber } from "../config/appsConfig";

/**
 * Footer est un composant qui affiche le pied de page de l'application.
 * Certaines parties (comme les liens vers le profil ou les formulaires) sont affichées uniquement si l'utilisateur est connecté.
 */

export const resolveFooterAppNumber = (currentAppNumber, storedApp, search) => {
  let resolved = currentAppNumber;
  if (resolved === null || resolved === undefined) {
    if (storedApp) {
      resolved = parseInt(storedApp, 10);
    } else {
      const urlParams = new URLSearchParams(search);
      const appParam = urlParams.get("app");
      /* istanbul ignore next */
      if (appParam) {
        resolved = parseInt(appParam, 10);
      }
    }
  }
  return resolved;
};

const Footer = () => {
  const year = new Date().getFullYear();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const appNumber = useSelector(selectAppNumber);

  // États dynamiques
  const [logo, setLogo] = useState("");
  const [footerText, setFooterText] = useState("");
  const [cguLink, setCguLink] = useState("");
  const [cgvLink, setCgvLink] = useState("");

  useEffect(() => {
    // Déterminer quel appNumber utiliser (Redux ou fallback localStorage)
    const storedApp = localStorage.getItem("devAppNumber");
    const currentAppNumber = resolveFooterAppNumber(
      appNumber,
      storedApp,
      window.location.search
    );

    // Utiliser appNumber pour déterminer le logo et les textes
    if (currentAppNumber === 2) {
      setLogo(require("../asset/logoKlesiaMPutBlanc.webp"));
      setFooterText(
        `© ${year} par Mes bons plans Klesia Mut. Tous droits réservés.`
      );
      setCguLink("/CGU.pdf");
      setCgvLink("/CGV.pdf");
    } else if (currentAppNumber === 3) {
      setLogo(require("../asset/logoBlancCarceptPrev.webp"));
      setFooterText(
        `© ${year} par Mes bons plans Klesia Carcept. Tous droits réservés.`
      );
      setCguLink("/CGU.pdf");
      setCgvLink("/CGV.pdf");
    } else if (currentAppNumber === 4) {
      // Utiliser le logo GHR (même logo que header pour l'instant, à adapter si vous avez un logo blanc)
      setLogo(require("../asset/GHR_logo.png"));
      setFooterText(`© ${year} par Mes bons plans GHR. Tous droits réservés.`);
      setCguLink("/CGU.pdf");
      setCgvLink("/CGV.pdf");
    } else if (currentAppNumber === 5) {
      // Utiliser le logo AGPM (même logo que header pour l'instant, à adapter si vous avez un logo blanc)
      setLogo(require("../asset/logo_agpm_blanc.webp"));
      setFooterText(`© ${year} par Mes bons plans AGPM. Tous droits réservés.`);
      setCguLink("/CGU.pdf");
      setCgvLink("/CGV.pdf");
    } else if (currentAppNumber === 6) {
      // Utiliser le logo MEDEF (même logo que header pour l'instant, à adapter si vous avez un logo blanc)
      setLogo(require("../asset/logo_medef.webp"));
      setFooterText(
        `© ${year} par Mes bons plans MEDEF. Tous droits réservés.`
      );
      setCguLink("/CGU.pdf");
      setCgvLink("/CGV.pdf");
    } else {
      // App 1 ou par défaut
      setLogo(require("../asset/logoKlesiaBlanc.webp"));
      setFooterText(
        `© ${year} par Mes bons plans Klesia Pro. Tous droits réservés.`
      );
      setCguLink("/CGU.pdf");
      setCgvLink("/CGV.pdf");
    }
  }, [year, appNumber]);

  const isApp4 = appNumber === 4;
  const isApp5 = appNumber === 5;
  const isApp6 = appNumber === 6;

  const contactEmail = getContactEmailByAppNumber(appNumber);

  return (
    <footer className="bg-theme-primary text-white py-8 md:py-10 px-4 md:px-6 min-h-[200px]">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
          {/* Logo Section */}
          <div className="flex justify-center md:justify-start items-start">
            <div
              className={
                isApp6
                  ? "bg-white rounded-xl px-4 py-3 flex items-center justify-center"
                  : ""
              }
            >
              <img
                src={logo}
                alt="LogoFooter"
                className={`h-auto ${
                  isApp4
                    ? "max-w-[220px] md:max-w-[250px] lg:max-w-[280px]"
                    : "max-w-[200px]"
                }`}
              />
            </div>
          </div>

          {/* Liens visibles uniquement si connecté */}
          {isLoggedIn && (
            <div className="text-center md:text-left">
              <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">
                Navigation
              </h3>
              <ul className="space-y-2 md:space-y-3">
                <li>
                  <NavLink
                    to="/Profil"
                    className="flex items-center justify-center md:justify-start gap-2 hover:text-theme-accent transition-colors text-sm md:text-base"
                  >
                    <i className="fas fa-user"></i> Mon compte
                  </NavLink>
                </li>
                <li>
                  <a
                    href={`mailto:${contactEmail}`}
                    className="flex items-center justify-center md:justify-start gap-2 hover:text-theme-accent transition-colors text-sm md:text-base"
                  >
                    <i className="fas fa-envelope"></i> Contact
                  </a>
                </li>
              </ul>
            </div>
          )}

          {/* Section toujours visible */}
          <div className="text-center md:text-left">
            <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">
              Informations légales
            </h3>
            <ul className="space-y-2 md:space-y-3">
              <li>
                <a
                  href="/Mentions"
                  className="flex items-center justify-center md:justify-start gap-2 hover:text-theme-accent transition-colors text-sm md:text-base"
                >
                  <i className="fas fa-file-alt"></i> Mentions légales
                </a>
              </li>
              <li>
                <a
                  href={cguLink}
                  download="CGU.pdf"
                  className="flex items-center justify-center md:justify-start gap-2 hover:text-theme-accent transition-colors text-sm md:text-base"
                >
                  <i className="fas fa-file-alt"></i> CGU
                </a>
              </li>
              <li>
                <a
                  href={cgvLink}
                  download="CGV.pdf"
                  className="flex items-center justify-center md:justify-start gap-2 hover:text-theme-accent transition-colors text-sm md:text-base"
                >
                  <i className="fas fa-file-alt"></i> CGV
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/20 pt-4 md:pt-6 text-center">
          <p
            className={`text-xs md:text-sm opacity-90 px-2 ${
              isApp5 || isApp6 ? "text-white" : ""
            }`}
          >
            {footerText}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
